<template>
  <tr class="bg-gray-50 hover:bg-gray-100 transition-colors duration-150 ease-in-out text-right child:py-4 first-child:pl-16 last-child:pr-3 text-sm">
    <td
      class="py-2 text-left pl-3"
      :aria-label="$t('ClaimCertifiedCosts.SharedFundCostRow.accessibility.sharedFundCode') + ': ' + $props.sharedFund.code + ', ' + $t('ClaimCertifiedCosts.SharedFundCostRow.accessibility.sharedFundName') + ': ' + $props.sharedFund.name"
    >
      <div class="flex items-start space-x-2">
        <span class="font-semibold text-gray-900 flex-shrink-0">{{ $props.sharedFund.code }}</span>

        <p class="w-60 text-gray-700">
          {{ $props.sharedFund.name }}
        </p>
      </div>
    </td>

    <td
      class="pr-3"
      :title="showBudgetCommentTooltip ? varianceComment?.body : ''"
      :aria-label="$t('ClaimCertifiedCosts.SharedFundCostRow.accessibility.budgetAmount') + ': ' + ($props.sharedFund.budget ? australianCurrency($props.sharedFund.budget) : '')"
    >
      <div class="flex items-center justify-end">
        <button
          v-if="showBudgetCommentButton"
          type="button"
          class="btn-icon btn-icon-default mr-1 shrink-0"
          :aria-label="varianceComment ? $t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.editComment') : $t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.addComment')"
          @click.stop="editBudgetComment"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
    />
</svg>
        </button>

        <button
          v-else-if="!!varianceComment?.body"
          type="button"
          class="btn-icon btn-icon-default mr-1 shrink-0"
          @click.stop="showBudgetComment"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
    />
</svg>
        </button>

        <div class="min-w-0">
          {{ australianCurrency($props.sharedFund.budget) }}
        </div>
      </div>
    </td>

    <td
      v-if="!typingClaim"
      :class="{ 'cursor-pointer': canEditClaim }"
      class="bg-green-200/50"
      :aria-label="$t('ClaimCertifiedCosts.SharedFundCostRow.accessibility.currentClaim') + ': ' + (canEditClaim ? australianCurrency(costForm.form.current_claim) : australianCurrency(sharedFundCost.current_claim))"
      @click="typeClaim"
    >
      <div class="group flex items-center justify-end">
        <button
          v-if="canEditClaim"
          type="button"
          class="btn-icon btn-icon-blue invisible group-hover:visible"
          :aria-label="$t('ClaimCertifiedCosts.SharedFundCostRow.accessibility.editClaim')"
        >
          <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="h-4 w-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
        </button>
        {{ canEditClaim ? australianCurrency(costForm.form.current_claim) : australianCurrency(sharedFundCost.current_claim) }}
      </div>
    </td>

    <td
      v-else-if="canEditClaim"
      class="bg-green-200/50"
    >
      <div class="w-1/2 ml-auto">
        <AppCurrencyInput
          ref="claimInput"
          v-model.number="costForm.form.current_claim"
          name="current_claim"
          type="number"
          min="1"
          data-test="quantity-input"
          class="w-20 h-6 form-input"
          :aria-label="$t('ClaimCertifiedCosts.SharedFundCostRow.accessibility.currentClaim')"
          @blur="stopTypingClaim"
          @keyup.enter="stopTypingClaim"
          @keyup.esc="typingClaim = false"
        />
      </div>
    </td>

    <td
      v-else
      class="bg-green-200/50"
    />

    <td
      v-if="sharedFundCost.current_certified && !typingCertified"
      :class="{ 'cursor-pointer': canEditCertified }"
      class="bg-green-200/50"
      :aria-label="$t('ClaimCertifiedCosts.SharedFundCostRow.accessibility.currentCertified') + ': ' + (canEditCertified ? australianCurrency(costForm.form.current_certified) : australianCurrency(sharedFundCost.current_certified))"
      @click="typeCertified"
    >
      <div class="group flex items-center justify-end">
        <button
          v-if="canEditCertified"
          type="button"
          class="btn-icon group-hover:visible btn-icon-blue invisible"
          :aria-label="$t('ClaimCertifiedCosts.SharedFundCostRow.accessibility.editCertified')"
        >
          <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="h-4 w-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
        </button>

        {{ canEditCertified ? australianCurrency(costForm.form.current_certified) : australianCurrency(sharedFundCost.current_certified) }}
      </div>
    </td>

    <td
      v-else-if="canEditCertified"
      class="bg-green-200/50"
    >
      <div class="w-1/2 ml-auto">
        <AppCurrencyInput
          ref="certifiedInput"
          v-model.number="costForm.form.current_certified"
          name="current_certified"
          type="number"
          min="1"
          data-test="quantity-input"
          class="w-20 h-6 form-input"
          :aria-label="$t('ClaimCertifiedCosts.SharedFundCostRow.accessibility.currentCertified')"
          @blur="stopTypingCertified"
          @keyup.enter="stopTypingCertified"
          @keyup.esc="typingCertified = false"
        />
      </div>
    </td>

    <td
      v-else
      class="bg-green-200/50"
    />

    <td
      class="group bg-green-200/50"
      :class="{ 'text-red-600': hasVariance && variance !== 0 }"
      :title="hasVariance && variance !== 0 && varianceComment?.body ? varianceComment.body : ''"
      :aria-label="$t('ClaimCertifiedCosts.SharedFundCostRow.accessibility.varianceTooltip') + ': ' + (hasVariance ? australianCurrency(variance) : '')"
    >
      <div class="flex items-center justify-end">
        <button
          v-if="showCommentButton"
          type="button"
          class="btn-icon group-hover:visible btn-icon-default invisible mr-1 shrink-0"
          :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.editComment')"
          @click.stop="editVarianceComment"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
    />
</svg>
        </button>

        <div class="min-w-0">
          {{ hasVariance ? australianCurrency(variance) : '' }}
        </div>
      </div>
    </td>

    <td
      class="bg-green-200/50"
      :aria-label="$t('ClaimCertifiedCosts.SharedFundCostRow.accessibility.previousAmount') + ': ' + australianCurrency(sharedFund.previous(claim))"
    >
      {{ australianCurrency(sharedFund.previous(claim)) }}
    </td>

    <td
      class="bg-green-200/50 pr-3"
      :aria-label="$t('ClaimCertifiedCosts.SharedFundCostRow.accessibility.cumulativeAmount') + ': ' + australianCurrency(sharedFund.previous(claim) + (sharedFundCost?.current_certified || 0))"
    >
      {{ australianCurrency(sharedFund.previous(claim) + (sharedFundCost?.current_certified || 0)) }}
    </td>

    <td
      class="bg-blue-100/50"
      :aria-label="$t('ClaimCertifiedCosts.SharedFundCostRow.accessibility.claimPercentage') + ': ' + (sharedFundCost && sharedFund.budget ? `${australianNumber((sharedFundCost.current_claim / sharedFund.budget) * 100)}%` : '')"
    >
      <span v-if="showPercentages">
        {{ sharedFundCost && sharedFund.budget ? `${australianNumber(((sharedFundCost.current_claim ?? 0) / sharedFund.budget) * 100)}%` : '' }}
      </span>
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
      :aria-label="$t('ClaimCertifiedCosts.SharedFundCostRow.accessibility.certifiedPercentage') + ': ' + (sharedFundCost && sharedFund.budget ? `${australianNumber((sharedFundCost.current_certified / sharedFund.budget) * 100)}%` : '')"
    >
      {{ sharedFundCost && sharedFund.budget ? `${australianNumber(((sharedFundCost.current_certified ?? 0) / sharedFund.budget) * 100)}%` : '' }}
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
      :aria-label="$t('ClaimCertifiedCosts.SharedFundCostRow.accessibility.previousPercentage') + ': ' + (sharedFund.budget ? australianNumber((sharedFund.previous(claim) / sharedFund.budget) * 100) + '%' : '')"
    >
      {{ sharedFund.budget ? australianNumber((sharedFund.previous(claim) / sharedFund.budget) * 100) : 0 }}%
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50 pr-3"
      :aria-label="$t('ClaimCertifiedCosts.SharedFundCostRow.accessibility.cumulativePercentage') + ': ' + (sharedFund.budget ? australianNumber(((sharedFund.previous(claim) + (sharedFundCost?.current_certified || 0)) / sharedFund.budget) * 100) + '%' : '')"
    >
      {{ sharedFund.budget ? australianNumber(((sharedFund.previous(claim) + (sharedFundCost?.current_certified || 0)) / sharedFund.budget) * 100) : 0 }}%
    </td>

    <td
      class="pr-3"
      :aria-label="$t('ClaimCertifiedCosts.SharedFundCostRow.accessibility.remainingBudget') + ': ' + australianCurrency(sharedFund.remaining(claim, sharedFundCost?.current_certified || 0))"
    >
      {{ australianCurrency(sharedFund.remaining(claim, sharedFundCost?.current_certified || 0)) }}
    </td>
  </tr>
</template>

<script setup lang="ts">
import { useFormData } from '@/composables/useFormData';
import { useSignedData } from '@/composables/useSignedData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, australianNumber, falsey, parseUrlParams } from '@/helpers';
import { t } from '@/i18n';
import Claim, { ClaimStatus } from '@/models/Claim';
import SharedFund from '@/models/SharedFund';
import SharedFundCost from '@/models/SharedFundCost';
import { useCommentsStore } from '@/store/comments';
import { useSharedFundCostsStore } from '@/store/sharedFundCosts';
import Swal from 'sweetalert2';
import { computed, nextTick, ref, watch } from 'vue';
import { toast } from 'vue3-toastify';

const props = defineProps<{
  sharedFundCost: SharedFundCost;
  sharedFund: SharedFund;
  claim: Claim;
  showPercentages?: boolean;
}>();

const sharedFundCostsStore = useSharedFundCostsStore();

const sharedFundCost = computed(() => {
  return props.sharedFundCost;
});

const variance = computed(() => {
  return (sharedFundCost.value.current_claim ?? 0) - (sharedFundCost.value.current_certified ?? 0);
});

const hasVariance = computed(() => {
  return !falsey(sharedFundCost.value.current_claim) && !falsey(sharedFundCost.value.current_certified);
});

const commentsStore = useCommentsStore();

const varianceComment = computed(() => {
  return commentsStore.models
    .where('commentable_id', props.sharedFundCost.id)
    .where('commentable_type', props.sharedFundCost.$entity())
    .where('creator_id', props.claim.id)
    .where('creator_type', props.claim.$entity())
    .first();
});

const typingClaim = ref(false);
const typingCertified = ref(false);
const signedParams = useSignedData();
const claimInput = ref();
const certifiedInput = ref();

const canEditClaim = computed(() => {
  return signedParams.meta.edit_claim &&
    (props.claim.status === ClaimStatus.contractor_claim || props.claim.status === ClaimStatus.draft_contractor);
});

const canEditCertified = computed(() => {
  return signedParams.meta.edit_certified && props.claim.status === ClaimStatus.pqs_review;
});

const typeClaim = () => {
  if(canEditClaim.value) {
    typingClaim.value = true;

    nextTick(() => {
      claimInput.value?.focusInput();
    });
  }
};

const typeCertified = () => {
  if(canEditCertified.value) {
    typingCertified.value = true;

    nextTick(() => {
      certifiedInput.value?.focusInput();
    });
  }
};

const saveClaimAction = useStoreApiAction(sharedFundCostsStore.saveClaim);
const saveCertifiedAction = useStoreApiAction(sharedFundCostsStore.saveCertified);
const storeSignedCommentAction = useStoreApiAction(sharedFundCostsStore.storeSignedComment);
const updateSignedCommentAction = useStoreApiAction(sharedFundCostsStore.updateSignedComment);

const claimParams = computed(() => {
  return parseUrlParams(signedParams.meta.shared_fund_claim_url as string);
});

const certifiedParams = computed(() => {
  return parseUrlParams(signedParams.meta.shared_fund_certified_url as string);
});

const storeCommentParams = computed(() => {
  return parseUrlParams(signedParams.meta.shared_fund_store_comment_url as string);
});

const updateCommentParams = computed(() => {
  return parseUrlParams(signedParams.meta.shared_fund_update_comment_url as string);
});

const showCommentButton = computed(() => {
  return hasVariance.value && variance.value !== 0 && !!varianceComment.value && canEditCertified.value;
});

const showBudgetCommentButton = computed(() => {
  return canEditCertified.value &&
    !falsey(sharedFundCost.value) &&
    !falsey(sharedFundCost.value.current_certified) &&
    !falsey(sharedFundCost.value.current_claim);
});

const showBudgetCommentTooltip = computed(() => {
  return !!varianceComment.value?.body && showBudgetCommentButton.value;
});

const saveSignedComment = (comment: string) => {
  return storeSignedCommentAction.request(props.claim.id, storeCommentParams.value, {
    shared_fund_cost_id: sharedFundCost.value.id,
    comment,
  });
};

const saveSignedCommentUpdate = (comment: string) => {
  return updateSignedCommentAction.request(props.claim.id, updateCommentParams.value, {
    shared_fund_cost_id: sharedFundCost.value.id,
    comment,
  });
};

const showBudgetComment = () => {
  Swal.fire({
    icon: 'info',
    title: (hasVariance.value && variance.value !== 0) ? 'Reason for Adjustment' : 'Comment',
    text: varianceComment.value?.body,
  });
};

const editBudgetComment = () => {
  Swal.fire({
    icon: 'info',
    title: (hasVariance.value && variance.value !== 0) ? 'Reason for Adjustment' : 'Comment',
    text: (hasVariance.value && variance.value !== 0) ?
      'Please provide a reason for the variance between the claim and certified amounts.' :
      'Provide an optional comment for the cost.',

    input: 'textarea',
    inputValue: varianceComment.value?.body ?? '',
    showCancelButton: true,
    confirmButtonText: varianceComment.value ? 'Update' : 'Save',
  }).then((result) => {
    if(result.isConfirmed && result.value) {
      if(varianceComment.value) {
        saveSignedCommentUpdate(result.value).then(() => {
          toast.success('Comment updated');
        });
      } else {
        saveSignedComment(result.value).then(() => {
          toast.success('Comment saved');
        });
      }
    }
  });
};

const editVarianceComment = () => {
  Swal.fire({
    icon: 'warning',
    title: t('ClaimCertifiedCosts.SharedFundCostRow.reasonAdjustmentTitle'),
    text: t('ClaimCertifiedCosts.SharedFundCostRow.reasonAdjustmentText'),
    input: 'textarea',
    inputValue: varianceComment.value?.body ?? '',
    showCancelButton: true,
    confirmButtonText: 'Update',
  }).then((result) => {
    if(result.isConfirmed && result.value) {
      saveSignedCommentUpdate(result.value).then(() => {
        toast.success('Comment updated');
      });
    }
  });
};

const costForm = useFormData({
  id: undefined,
  current_certified: 0,
  current_claim: 0,
  shared_fund_id: undefined,
  comment: undefined,
});

watch(sharedFundCost, (newValue) => {
  if(newValue) {
    if(canEditCertified.value) {
      costForm.setData({
        id: newValue.id,
        current_certified: newValue.current_certified || 0,
      });
    } else if(canEditClaim.value) {
      costForm.setData({
        id: newValue.id,
        current_claim: newValue.current_claim || 0,
      });
    }
  }
}, { immediate: true });

const stopTypingClaim = () => {
  if(costForm.form.current_claim !== sharedFundCost.value.current_claim) {
    saveClaimAction.request(props.claim.id, claimParams.value, costForm.form).catch(() => {
      Swal.fire({
        icon: 'error',
        title: t('ClaimCertifiedCosts.SharedFundCostRow.errorTitle'),
      });
    });
  }

  typingClaim.value = false;
};

const stopTypingCertified = async () => {
  if(costForm.form.current_certified !== sharedFundCost.value.current_certified) {
    if(costForm.form.current_certified !== sharedFundCost.value.current_claim) {
      const comment = !varianceComment.value ?
        await Swal.fire({
          icon: 'warning',
          title: t('ClaimCertifiedCosts.SharedFundCostRow.reasonAdjustmentTitle'),
          text: t('ClaimCertifiedCosts.SharedFundCostRow.reasonAdjustmentText'),
          input: 'text',
        }) :
        undefined;

      costForm.form.comment = comment?.value || varianceComment.value?.body;
    } else {
      costForm.form.comment = undefined;
    }

    saveCertifiedAction.request(props.claim.id, certifiedParams.value, costForm.form).catch(() => {
      Swal.fire({
        icon: 'error',
        title: t('ClaimCertifiedCosts.SharedFundCostRow.errorTitle'),
      });

      costForm.form.current_certified = sharedFundCost.value.current_certified;
    });
  }

  typingCertified.value = false;
};
</script>

<style scoped></style>
